import { useNavigate, useParams } from 'react-router-dom';
import { matchdayApi, teamApi } from '@/api/endpoints';
import type {
  BonusOverviewEntry,
  Match,
  Matchday,
  MatchdayOverview,
  OverviewEntry,
  OverviewTips,
  Player,
  Question,
  Team,
} from '@/api/types';
import { Alert, Card, Spinner } from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { BonusOverviewPage } from '@/features/bonus/BonusOverviewPage';
import { answerLabel, questionHeaderLabel } from '@/features/questions/questionLabels';
import { shortIntervalLabel } from '@/lib/attendance';
import { formatDateTime, isLiveWindow, isPastDeadline } from '@/lib/datetime';
import { pickCurrentMatchday, sortChronologically } from '@/lib/matchday';
import { truncateForOverview } from '@/lib/text';
import { useAsync } from '@/lib/useAsync';
import adminStyles from '@/features/admin/admin.module.css';
import { MatchdayNavigator } from './MatchdayNavigator';
import styles from './ergebnistipp.module.css';

/** Zeilenhintergrund des angemeldeten Nutzers in der Tippübersicht (Grün, ~95% Transparenz). */
const SELF_ROW_BACKGROUND = 'rgba(10, 122, 58, 0.05)';
/**
 * Deckende Vorab-Mischung von SELF_ROW_BACKGROUND über Kartenweiß (#fff) — nur
 * für die rechts fixierte G-Spalte: die durchsichtige Variante würde beim
 * seitlichen Scrollen den Inhalt der darunter wegscrollenden Spalten
 * durchscheinen lassen (position: sticky legt die Spalte optisch darüber).
 */
const SELF_ROW_STICKY_BACKGROUND = '#f3f8f5';

interface LoadedData {
  overview: MatchdayOverview;
  players: Player[];
  match: Match | null;
  /** Individuelle Fragen dieses Spieltags (i.d.R. leer — opt-in per Admin). */
  questions: Question[];
  questionEntries: BonusOverviewEntry[];
  teams: Team[];
}

/** Spieltagspunkte (P): Summe der vier Kategorie-Punkte eines Mitspielers. */
function sumPoints(tips: OverviewTips | null): number {
  if (!tips) return 0;
  return (
    (tips.result?.points ?? 0) +
    (tips.attendance?.points ?? 0) +
    (tips.scorer?.points ?? 0) +
    (tips.assist?.points ?? 0)
  );
}

/**
 * Vor Tippschluss: „Wer hat getippt" (Fremd-Tipps verdeckt, §13.0).
 * Nach Tippschluss: volle Tippübersicht als Raster — je Mitspieler Tipp + Punkte
 * je Kategorie sowie die Spieltagspunkte (P), wie in der kicktipp-Vorlage.
 */
export function MatchdayOverviewPage() {
  const { id } = useParams<{ id: string }>();
  const matchdayId = Number(id);
  const navigate = useNavigate();

  // Alle Spieltage laden — einerseits um Bonus-Übersicht (Fragen-Raster) von der
  // Ergebnis-/Kategorie-Übersicht zu trennen, andererseits als Grundlage für die
  // Vor/Zurück-Navigation, das Spieltags-Dropdown und den Reiter-Wechsel.
  const head = useAsync<Matchday[]>((signal) => matchdayApi.list(signal), []);

  if (Number.isNaN(matchdayId)) return <Alert>Ungültiger Spieltag.</Alert>;
  if (head.loading) return <Spinner label="Übersicht wird geladen…" />;
  if (head.error) return <Alert>{head.error}</Alert>;
  if (!head.data) return <Alert>Keine Daten.</Alert>;

  const matchday = head.data.find((m) => m.id === matchdayId);
  if (!matchday) return <Alert>Spieltag nicht gefunden.</Alert>;

  const navMatchdays = sortChronologically(head.data.filter((m) => m.kind !== 'bonus'));
  const bonusMatchday = sortChronologically(head.data.filter((m) => m.kind === 'bonus'))[0] ?? null;
  const isBonus = matchday.kind === 'bonus';

  const showResultTab = () => {
    const target = pickCurrentMatchday(navMatchdays);
    if (target) navigate(`/matchdays/${target.id}/overview`);
  };
  const showBonusTab = () => {
    if (bonusMatchday) navigate(`/matchdays/${bonusMatchday.id}/overview`);
  };

  return (
    <>
      <h1>Tippübersicht</h1>
      <div className={styles.tabBar}>
        <button
          type="button"
          className={`${styles.tab} ${!isBonus ? styles.tabActive : ''}`}
          disabled={navMatchdays.length === 0}
          onClick={showResultTab}
        >
          Spieltag
        </button>
        <button
          type="button"
          className={`${styles.tab} ${isBonus ? styles.tabActive : ''}`}
          disabled={!bonusMatchday}
          onClick={showBonusTab}
        >
          Bonusfragen
        </button>
      </div>

      {isBonus ? (
        <BonusOverviewPage matchday={matchday} showHeader={false} />
      ) : (
        <ResultOverview matchdayId={matchdayId} navMatchdays={navMatchdays} />
      )}
    </>
  );
}

function ResultOverview({
  matchdayId,
  navMatchdays,
}: {
  matchdayId: number;
  navMatchdays: Matchday[];
}) {
  const { user } = useAuth();

  const { data, loading, error } = useAsync<LoadedData>(
    async (signal) => {
      const [overview, players, matches, questionsOverview, teams] = await Promise.all([
        matchdayApi.overview(matchdayId, signal),
        matchdayApi.players(matchdayId, signal).catch(() => [] as Player[]),
        matchdayApi.matches(matchdayId, signal).catch(() => [] as Match[]),
        matchdayApi.questionsOverview(matchdayId, signal).catch(() => null),
        teamApi.list(signal).catch(() => [] as Team[]),
      ]);
      return {
        overview,
        players,
        match: matches[0] ?? null,
        questions: [...(questionsOverview?.questions ?? [])].sort(
          (a, b) => a.sort_order - b.sort_order,
        ),
        questionEntries: questionsOverview?.overview ?? [],
        teams,
      };
    },
    [matchdayId],
    { refreshOnFocus: true },
  );

  if (loading) return <Spinner label="Übersicht wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Alert>Keine Daten.</Alert>;

  const { overview, players, match, questions, questionEntries, teams } = data;
  const { matchday } = overview;
  const entries = overview.overview;
  const deadlinePassed = overview.deadline_passed ?? isPastDeadline(matchday.deadline_at);

  const playerLabel = (pid: number | null): string => {
    if (pid === null) return 'KEIN';
    const p = players.find((pl) => pl.id === pid);
    return p?.short_code ?? p?.name ?? `#${pid}`;
  };

  const teamName = (id: number | null | undefined): string => {
    if (id == null) return '—';
    const t = teams.find((x) => x.id === id);
    return t?.short_name ?? t?.name ?? `#${id}`;
  };

  const questionEntryByUser = new Map(questionEntries.map((e) => [e.user_id, e] as const));

  return (
    <>
      <MatchdayNavigator current={matchday} all={navMatchdays} linkSuffix="/overview" />

      <MatchFixtureHeader match={match} playerLabel={playerLabel} />

      {!deadlinePassed ? (
        <PendingTable
          entries={entries}
          playerLabel={playerLabel}
          selfId={user?.id}
          questions={questions}
          questionEntryByUser={questionEntryByUser}
          teamName={teamName}
        />
      ) : (
        <ResultGrid
          entries={entries}
          playerLabel={playerLabel}
          selfId={user?.id}
          questions={questions}
          questionEntryByUser={questionEntryByUser}
          teamName={teamName}
        />
      )}
    </>
  );
}

/** Begegnung des Spieltags — immer ganz oben, unabhängig vom Tippschluss. */
function MatchFixtureHeader({
  match,
  playerLabel,
}: {
  match: Match | null;
  playerLabel: (pid: number | null) => string;
}) {
  if (!match) return null;

  const finished =
    match.status === 'finished' && match.home_goals !== null && match.away_goals !== null;
  // Ab Anpfiff läuft das Spiel: Backend setzt es per Cron auf 'live' + 0:0, aber
  // damit die Übersicht SOFORT (unabhängig vom Cron-Takt) 0:0 zeigt, leiten wir
  // "live" auch aus der Anpfiffzeit ab — aber nur innerhalb eines Zeitfensters,
  // damit ein altes, nie auf 'finished' gesyncte Spiel nicht ewig "live" bleibt
  // (spiegelt das Fenster im Backend-ResultSyncService). Fehlt der Score, 0:0.
  const live = !finished && (match.status === 'live' || isLiveWindow(match.kickoff_at));
  const scoreText =
    finished || live ? `${match.home_goals ?? 0}:${match.away_goals ?? 0}` : '–:–';
  // null = vom Admin noch nicht bestätigt (kein "keiner"!), [] = bestätigt: keiner.
  const namesOf = (ids: number[] | null): string => {
    if (ids === null) return 'noch nicht gepflegt';
    return ids.length > 0 ? ids.map((pid) => playerLabel(pid)).join(', ') : 'keiner';
  };

  return (
    <Card>
      <div className={styles.match}>
        <div className={styles.matchKickoff}>
          {live ? <span className={styles.liveBadge}>● LIVE</span> : formatDateTime(match.kickoff_at)}
        </div>

        <div className={`${styles.team} ${styles.teamHome}`}>
          <span className={styles.teamName}>{match.home_team.name}</span>
          {match.home_team.logo_url && (
            <img className={styles.logo} src={match.home_team.logo_url} alt="" />
          )}
        </div>

        <div className={styles.colon}>{scoreText}</div>

        <div className={`${styles.team} ${styles.teamAway}`}>
          {match.away_team.logo_url && (
            <img className={styles.logo} src={match.away_team.logo_url} alt="" />
          )}
          <span className={styles.teamName}>{match.away_team.name}</span>
        </div>
      </div>

      {/* Ist-Werte des Spiels als Label/Wert-Paare — Label als ruhiger Eyebrow
          wie die Anpfiff-Zeile, Wert in Vereinsgrün. Gold bleibt den erzielten
          Punkten vorbehalten (design-system). Es wird NUR gezeigt, was der Admin
          wirklich erfasst hat: jede Kategorie erscheint erst mit ihrem Wert,
          "noch nicht gepflegt" taucht nicht mehr auf. */}
      {(match.attendance != null ||
        match.scorer_player_ids !== null ||
        match.assist_player_ids !== null) && (
        <div className={styles.solutionRow} style={{ marginTop: 12 }}>
          {match.attendance != null && (
            <>
              <span className={styles.solutionLabel}>Zuschauer</span>
              <span className={styles.solutionValue}>
                {match.attendance.toLocaleString('de-DE')}
              </span>
            </>
          )}
          {match.scorer_player_ids !== null && (
            <>
              <span className={styles.solutionLabel}>Tor</span>
              <span className={styles.solutionValue}>{namesOf(match.scorer_player_ids)}</span>
            </>
          )}
          {match.assist_player_ids !== null && (
            <>
              <span className={styles.solutionLabel}>Assist</span>
              <span className={styles.solutionValue}>{namesOf(match.assist_player_ids)}</span>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

/** Tabelle vor Tippschluss: alle Teilnehmer, eigene Tipps sichtbar, fremde verdeckt (§13.0). */
function PendingTable({
  entries,
  playerLabel,
  selfId,
  questions,
  questionEntryByUser,
  teamName,
}: {
  entries: OverviewEntry[];
  playerLabel: (pid: number | null) => string;
  selfId: number | undefined;
  questions: Question[];
  questionEntryByUser: Map<number, BonusOverviewEntry>;
  teamName: (id: number | null | undefined) => string;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <p className="muted">Noch keine Mitspieler vorhanden.</p>
      </Card>
    );
  }

  const rows = [...entries].sort(
    (a, b) =>
      (b.standings?.total ?? 0) - (a.standings?.total ?? 0) ||
      a.display_name.localeCompare(b.display_name),
  );

  return (
    <Card className={styles.tableCard}>
      <div style={{ overflowX: 'auto' }}>
        <table className={`${adminStyles.rankTable} ${styles.overviewTable} ${styles.noRankLeader}`}>
          <thead>
            <tr>
              <th className={adminStyles.rankPos}>#</th>
              <th className={styles.nameCol}>Name</th>
              <th className={styles.tipCol}>Erg.</th>
              <th className={styles.tipCol}>Zusch.</th>
              <th className={styles.tipCol}>Tor</th>
              <th className={styles.tipCol}>Assist</th>
              {questions.map((q, i) => (
                <th key={q.id} className={styles.tipCol} title={q.prompt}>
                  {questionHeaderLabel(q, i)}
                </th>
              ))}
              <th className={styles.numCol}>P</th>
              <th className={styles.numCol}>B</th>
              <th className={styles.numCol}>S</th>
              <th className={styles.numCol}>G</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const isSelf = entry.user_id === selfId;
              const t = isSelf ? entry.tips : null;
              const s = entry.standings;
              const hidden = !isSelf && entry.has_tipped;
              const selfRowStyle = isSelf ? { background: SELF_ROW_BACKGROUND } : undefined;
              const qEntry = questionEntryByUser.get(entry.user_id);
              const qHidden = !isSelf && (qEntry?.has_tipped ?? false);
              const qAnswerByQuestion = new Map(
                (qEntry?.answers ?? []).map((a) => [a.question_id, a] as const),
              );
              return (
                <tr key={entry.user_id}>
                  <td className={adminStyles.rankPos} style={selfRowStyle}>
                    {s?.rank ?? '—'}
                  </td>
                  <td
                    className={`${styles.nameCol} ${isSelf ? styles.overviewSelf : ''}`}
                    style={selfRowStyle}
                  >
                    {truncateForOverview(entry.display_name)}
                    {entry.kackhaufen ? ' 💩' : ''}
                  </td>
                  <td className={styles.tipCol} style={selfRowStyle}>
                    {t?.result
                      ? `${t.result.home_pred}:${t.result.away_pred}`
                      : hidden
                        ? '—'
                        : ''}
                  </td>
                  <td className={styles.tipCol} style={selfRowStyle}>
                    {t?.attendance
                      ? shortIntervalLabel(t.attendance.interval_index)
                      : hidden
                        ? '—'
                        : ''}
                  </td>
                  <td className={styles.tipCol} style={selfRowStyle}>
                    {t?.scorer ? playerLabel(t.scorer.player_id) : hidden ? '—' : ''}
                  </td>
                  <td className={styles.tipCol} style={selfRowStyle}>
                    {t?.assist ? playerLabel(t.assist.player_id) : hidden ? '—' : ''}
                  </td>
                  {questions.map((q) => {
                    const a = qAnswerByQuestion.get(q.id);
                    return (
                      <td key={q.id} className={styles.tipCol} style={selfRowStyle}>
                        {a ? answerLabel(q, a, teamName) : qHidden ? '—' : ''}
                      </td>
                    );
                  })}
                  {/* Vor Tippschluss gibt es noch keine Spieltagspunkte (points
                      sind bis zur Wertung null) — "—" statt einer irreführenden 0. */}
                  <td className={`${styles.numCol} ${styles.numColStrong}`} style={selfRowStyle}>
                    —
                  </td>
                  <td className={styles.numCol} style={selfRowStyle}>
                    {s?.bonus ?? '—'}
                  </td>
                  <td className={styles.numCol} style={selfRowStyle}>
                    {s ? formatSiegpunkte(s.siegpunkte) : '—'}
                  </td>
                  <td
                    className={`${styles.numCol} ${styles.numColStrong}`}
                    style={isSelf ? { background: SELF_ROW_STICKY_BACKGROUND } : undefined}
                  >
                    {s?.total ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Tipp-Wert mit erzielten Punkten als Hochzahl (wie kicktipp). */
function TipCell({ value, points }: { value: string; points: number | null | undefined }) {
  return (
    <span>
      {value}
      {points != null && <sup className={styles.tipPoints}>{points}</sup>}
    </span>
  );
}

/** Rangänderung ggü. dem vorherigen Spieltag: ▲ besser, ▼ schlechter, sonst leer. */
function RankChange({ rank, previousRank }: { rank: number; previousRank: number | null }) {
  if (previousRank == null) return null;
  const delta = previousRank - rank;
  if (delta === 0) return null;
  return (
    <span className={delta > 0 ? styles.rankUp : styles.rankDown}>
      {delta > 0 ? '▲' : '▼'}
      {Math.abs(delta)}
    </span>
  );
}

function formatSiegpunkte(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Nach Tippschluss: Season-Tabelle (sortiert nach Gesamt) mit den Tipps dieses Spieltags. */
function ResultGrid({
  entries,
  playerLabel,
  selfId,
  questions,
  questionEntryByUser,
  teamName,
}: {
  entries: OverviewEntry[];
  playerLabel: (pid: number | null) => string;
  selfId: number | undefined;
  questions: Question[];
  questionEntryByUser: Map<number, BonusOverviewEntry>;
  teamName: (id: number | null | undefined) => string;
}) {
  const rows = [...entries].sort(
    (a, b) =>
      (b.standings?.total ?? 0) - (a.standings?.total ?? 0) ||
      a.display_name.localeCompare(b.display_name),
  );

  // Spieltagssieger (Gold) = hoechste Spieltagspunkte (P) dieses Spieltags, nicht
  // die Gesamttabellen-Position -- bei Gleichstand mehrere Gewinner (wie bei den
  // Siegpunkten selbst, Contract §14.0). Einmal vorab je User berechnet (statt
  // pro Zeile neu), damit das Maximum vor dem Rendern feststeht.
  const entryData = new Map(
    entries.map((entry) => {
      const qAnswerByQuestion = new Map(
        (questionEntryByUser.get(entry.user_id)?.answers ?? []).map(
          (a) => [a.question_id, a] as const,
        ),
      );
      const qPoints = questions.reduce(
        (acc, q) => acc + (qAnswerByQuestion.get(q.id)?.points ?? 0),
        0,
      );
      const p = sumPoints(entry.tips) + qPoints;
      return [entry.user_id, { qAnswerByQuestion, p }] as const;
    }),
  );
  const maxP = Math.max(0, ...[...entryData.values()].map((d) => d.p));

  return (
    <>
      <Card className={styles.tableCard}>
        <div style={{ overflowX: 'auto' }}>
          <table
            className={`${adminStyles.rankTable} ${styles.overviewTable} ${styles.noRankLeader}`}
          >
            <thead>
              <tr>
                <th className={adminStyles.rankPos}>#</th>
                <th className={styles.nameCol}>Name</th>
                <th className={styles.tipCol}>Erg.</th>
                <th className={styles.tipCol}>Zusch.</th>
                <th className={styles.tipCol}>Tor</th>
                <th className={styles.tipCol}>Assist</th>
                {questions.map((q, i) => (
                  <th key={q.id} className={styles.tipCol} title={q.prompt}>
                    {questionHeaderLabel(q, i)}
                  </th>
                ))}
                <th className={styles.numCol}>P</th>
                <th className={styles.numCol}>B</th>
                <th className={styles.numCol}>S</th>
                <th className={styles.numCol}>G</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => {
                const t = entry.tips;
                const s = entry.standings;
                const isSelf = entry.user_id === selfId;
                const selfRowStyle = isSelf ? { background: SELF_ROW_BACKGROUND } : undefined;
                const { qAnswerByQuestion, p } = entryData.get(entry.user_id)!;
                const isMatchdayWinner = maxP > 0 && p === maxP;
                return (
                  <tr key={entry.user_id} className={isMatchdayWinner ? styles.matchdayWinner : undefined}>
                    <td className={adminStyles.rankPos} style={selfRowStyle}>
                      {s?.rank ?? '—'}
                      {s && <RankChange rank={s.rank} previousRank={s.previous_rank} />}
                    </td>
                    <td
                      className={`${styles.nameCol} ${isSelf ? styles.overviewSelf : ''}`}
                      style={selfRowStyle}
                    >
                      {truncateForOverview(entry.display_name)}
                      {entry.kackhaufen ? ' 💩' : ''}
                    </td>
                    <td className={styles.tipCol} style={selfRowStyle}>
                      {t?.result ? (
                        <TipCell
                          value={`${t.result.home_pred}:${t.result.away_pred}`}
                          points={t.result.points}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.tipCol} style={selfRowStyle}>
                      {t?.attendance ? (
                        <TipCell
                          value={shortIntervalLabel(t.attendance.interval_index)}
                          points={t.attendance.points}
                        />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.tipCol} style={selfRowStyle}>
                      {t?.scorer ? (
                        <TipCell value={playerLabel(t.scorer.player_id)} points={t.scorer.points} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.tipCol} style={selfRowStyle}>
                      {t?.assist ? (
                        <TipCell value={playerLabel(t.assist.player_id)} points={t.assist.points} />
                      ) : (
                        '—'
                      )}
                    </td>
                    {questions.map((q) => {
                      const a = qAnswerByQuestion.get(q.id);
                      return (
                        <td key={q.id} className={styles.tipCol} style={selfRowStyle}>
                          {a ? (
                            <TipCell value={answerLabel(q, a, teamName)} points={a.points} />
                          ) : (
                            '—'
                          )}
                        </td>
                      );
                    })}
                    <td className={`${styles.numCol} ${styles.numColStrong}`} style={selfRowStyle}>
                      {p}
                    </td>
                    <td className={styles.numCol} style={selfRowStyle}>
                      {s?.bonus ?? '—'}
                    </td>
                    <td className={styles.numCol} style={selfRowStyle}>
                      {s ? formatSiegpunkte(s.siegpunkte) : '—'}
                    </td>
                    <td
                      className={`${styles.numCol} ${styles.numColStrong}`}
                      style={isSelf ? { background: SELF_ROW_STICKY_BACKGROUND } : undefined}
                    >
                      {s?.total ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
